import React from "react";
import styled, { css } from "styled-components";
import { Styleguide, theme } from "../../theme";
import { ResponsiveValue, ThemeableValue } from "../../types/style-types";

type Size = "small" | "medium" | "large" | "xlarge" | "xxlarge";
export type BaseProps = {
  color?: ThemeableValue;
  size?: ResponsiveValue<Size>;
};
type IconProps = BaseProps & {
  children: React.ReactElement;
};
type StyledProps = {
  iconColor: NonNullable<BaseProps["color"]>;
  size: NonNullable<BaseProps["size"]>;
};

const sizes: Record<Size, number> = {
  small: 20,
  medium: 24,
  large: 28,
  xlarge: 32,
  xxlarge: 40
};

const Svg = styled.svg<StyledProps>`
  flex-shrink: 0;

  ${({ iconColor }) => {
    if (typeof iconColor === "string") {
      return css`
        color: ${iconColor};
      `;
    }

    return css`
      color: ${theme(iconColor.light, iconColor.dark)};
    `;
  }}

  ${({ size }) =>
    typeof size === "string" &&
    css`
      width: ${sizes[size]}px;
      height: ${sizes[size]}px;
    `}

  ${({ size }) =>
    typeof size !== "string" &&
    size.mobile &&
    css`
      width: ${sizes[size.mobile]}px;
      height: ${sizes[size.mobile]}px;
    `}

  ${({ size }) =>
    typeof size !== "string" &&
    size.desktop &&
    css`
      @media (min-width: ${Styleguide.breakpoints.desktop}) {
        width: ${sizes[size.desktop]}px;
        height: ${sizes[size.desktop]}px;
      }
    `}
`;

function Icon({
  children,
  color = "currentColor",
  size = "medium"
}: IconProps) {
  return (
    <Svg
      size={size}
      iconColor={color}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {children}
    </Svg>
  );
}

export default Icon;
